// src/pages/TermsPage.jsx

import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Divider,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import { ThemeModeProvider } from "../components/ThemeModeContext";
import ThemeProviderWrapper from "../components/ThemeProviderWrapper";
import FeedbackButton from "../components/FeedbackButton";

// Componente auxiliar para conteúdo das Abas
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`terms-tabpanel-${index}`}
      aria-labelledby={`terms-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" component="div">
            {children}
          </Typography>
        </Box>
      )}
    </div>
  );
};

const TermsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const handleGoBack = () => {
    navigate(-1); // Volta para a página anterior
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <ThemeModeProvider>
      <ThemeProviderWrapper>
        <Box
          sx={{
            minHeight: "100vh",
            width: "100vw",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            background:
              theme.palette.mode === "light"
                ? `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`
                : `linear-gradient(135deg, ${theme.palette.background.default}, ${theme.palette.background.paper})`,
            color: "text.primary",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Paper
            elevation={4}
            sx={{
              width: "100%",
              maxWidth: 800,
              bgcolor: "background.paper",
              color: "text.primary",
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <Typography variant="h4" gutterBottom>
              Terms of Service
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Abas para alternar entre Usuários e Empresas */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              textColor="primary"
              indicatorColor="primary"
              sx={{ mb: 3 }}
            >
              <Tab label="Usuários" />
              <Tab label="Empresas" />
            </Tabs>

            {/* Conteúdo da Aba: Usuários */}
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h6" gutterBottom>
                Termos de Serviço para Usuários
              </Typography>

              {/* ================== TEXTO COMPLETO DOS TERMOS DE USUÁRIOS ================== */}
              <Typography variant="body2" paragraph>
                <strong>1. Aceitação dos Termos</strong><br />
                Ao acessar, registrar-se ou utilizar qualquer serviço fornecido pela nossa plataforma,
                o usuário concorda de maneira irrevogável e incondicional com os Termos de Serviço
                aqui descritos. Essa concordância abrange todos os aspectos da utilização da plataforma,
                incluindo, mas não se limitando a, interações com empresas parceiras, realização de
                transações financeiras, cadastro de informações pessoais e aceitação de políticas de
                privacidade.
                <br /><br />
                O usuário reconhece que a aceitação destes Termos é requisito indispensável para o uso
                da plataforma e que a continuidade de uso representa consentimento pleno e irrestrito.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>2. Alterações e Restrições de Serviço</strong><br />
                A plataforma se reserva o direito de, a qualquer momento e sem aviso prévio:
              </Typography>
              <ul>
                <li>Modificar, suspender ou encerrar quaisquer serviços, funcionalidades ou seções do site,
                  seja temporária ou permanentemente.</li>
                <li>Alterar as condições de uso, políticas aplicáveis ou Termos de Serviço, conforme
                  necessário para atender às exigências legais, operacionais ou comerciais.</li>
                <li>Encerrar ou suspender contas de usuários que violem qualquer termo descrito neste
                  documento ou apresentem comportamento incompatível com as regras de conduta estabelecidas.</li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>3. Encerramento e Bloqueio de Contas</strong><br />
                A plataforma pode, a seu exclusivo critério, suspender ou encerrar contas de usuários em caso de:
              </Typography>
              <ul>
                <li>Informações cadastrais falsas, incompletas ou fraudulentas.</li>
                <li>Violações destes Termos ou de quaisquer leis aplicáveis.</li>
                <li>Uso indevido dos serviços ou da plataforma.</li>
              </ul>
              <Typography variant="body2" paragraph>
                Além disso:
                <ul>
                  <li>
                    Contas encerradas ou bloqueadas podem ter o CPF do titular incluído em uma "lista de usuários
                    que falsificaram dados". Essa medida é necessária para combater fraudes e proteger a integridade
                    da plataforma e de seus usuários.
                  </li>
                  <li>
                    Qualquer titular de dados informados tem o direito de solicitar verificação das informações
                    cadastradas para comprovação de autenticidade. A plataforma se compromete a avaliar tais
                    solicitações de forma imparcial e criteriosa.
                  </li>
                </ul>
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>4. Transações e Taxas</strong><br />
                Ao utilizar os serviços oferecidos na plataforma, o usuário compreende e concorda que:
              </Typography>
              <ul>
                <li>
                  Qualquer transação realizada pode estar sujeita a impostos, tarifas ou taxas não explicitamente
                  detalhadas ao consumidor, mas que já estão embutidas no valor final apresentado.
                </li>
                <li>
                  O pagamento realizado pelo consumidor será exclusivamente para os serviços agendados e aprovados,
                  sem custos adicionais ocultos.
                </li>
              </ul>
              <Typography variant="body2" paragraph>
                Importante: O usuário declara estar ciente de que a plataforma não se responsabiliza por interpretações
                incorretas relacionadas a preços, taxas ou condições de pagamento.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>5. Denúncia e Fiscalização</strong><br />
                Os usuários têm o direito de denunciar empresas parceiras ou prestadores de serviços por quaisquer
                práticas que considerem inadequadas, antiéticas ou fraudulentas. Tais denúncias devem ser feitas
                diretamente através dos canais de atendimento da plataforma.
              </Typography>
              <Typography variant="body2" paragraph>
                A plataforma avaliará cada denúncia de maneira rigorosa e imparcial, podendo aplicar penalidades às
                empresas envolvidas, como suspensão, bloqueio ou encerramento de contas. Durante o processo de apuração,
                o denunciante será notificado sobre os avanços e resultados, garantindo a transparência do procedimento.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>6. Arbitragem Obrigatória e Renúncia ao Litígio</strong><br />
                Qualquer controvérsia ou disputa relacionada ao uso da plataforma, aos serviços fornecidos ou a quaisquer
                outras interações com a plataforma deverá ser resolvida exclusivamente por meio de arbitragem obrigatória.
              </Typography>
              <Typography variant="body2" paragraph>
                Ao aceitar estes Termos, o usuário renuncia:
              </Typography>
              <ul>
                <li>
                  Ao direito de processar a plataforma, empresas parceiras ou qualquer entidade afiliada diretamente em
                  tribunais convencionais.
                </li>
                <li>
                  Ao direito de recorrer a litígios antes de esgotar as possibilidades de negociação e acordo direto com
                  a plataforma e, se necessário, com as empresas envolvidas.
                </li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>7. Relacionamento com Empresas Parceiras</strong><br />
                Ao aceitar um serviço por meio da plataforma, o usuário declara que:
              </Typography>
              <ul>
                <li>
                  Lê, compreende e concorda com os Termos de Serviço estabelecidos pela empresa fornecedora do serviço
                  solicitado.
                </li>
                <li>
                  Não poderá processar diretamente as empresas parceiras sem negociação prévia com a plataforma e com a
                  empresa envolvida.
                </li>
                <li>
                  Qualquer tentativa de litígio direto com a empresa fornecedora será considerada uma violação destes
                  Termos, resultando em medidas como o bloqueio imediato da conta.
                </li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>8. Combate a Fraudes e Garantia de Segurança</strong><br />
                Para garantir a integridade da plataforma e proteger os usuários:
              </Typography>
              <ul>
                <li>
                  A plataforma utiliza tecnologias avançadas para monitorar e detectar atividades fraudulentas ou suspeitas.
                  Isso inclui, mas não se limita a, análise de padrões de uso, verificação de informações cadastrais e
                  rastreamento de transações.
                </li>
                <li>
                  Em casos de detecção de fraudes, a plataforma pode colaborar com autoridades legais, fornecendo
                  informações e documentos necessários para a investigação.
                </li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>9. Direitos e Obrigações do Usuário</strong><br />
                O usuário tem o direito de:
              </Typography>
              <ul>
                <li>
                  Usar a plataforma para agendar serviços de empresas parceiras de forma legítima e respeitando os Termos.
                </li>
                <li>
                  Denunciar práticas indevidas ou questionáveis de empresas parceiras ou de outros usuários.
                </li>
              </ul>

              <Typography variant="body2" paragraph>
                Por outro lado, o usuário tem a obrigação de:
              </Typography>
              <ul>
                <li>
                  Fornecer informações cadastrais verdadeiras, completas e atualizadas.
                </li>
                <li>
                  Utilizar a plataforma de maneira ética, sem violar os direitos de outros usuários, empresas ou da
                  própria plataforma.
                </li>
              </ul>
              <Typography variant="body2" paragraph>
                Nota Importante: A omissão ou falsificação de informações pode resultar no encerramento imediato da conta
                e na inclusão do CPF do usuário na lista de restrições.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>10. Considerações Finais</strong><br />
                A aceitação destes Termos é vinculativa e irrevogável. Ao utilizar a plataforma, o usuário declara que
                entende e aceita todos os termos, mesmo que não os tenha lido em sua totalidade. Recomenda-se a leitura
                periódica deste documento, pois ele pode ser atualizado sem aviso prévio.
              </Typography>
              <Typography variant="body2" paragraph>
                A plataforma se compromete a manter a transparência e a ética em todas as suas operações, garantindo um
                ambiente seguro e confiável para usuários e empresas parceiras.
              </Typography>
            </TabPanel>

            {/* Conteúdo da Aba: Empresas */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" gutterBottom>
                Termos de Serviço para Empresas
              </Typography>

              {/* ================== TEXTO COMPLETO DOS TERMOS DE EMPRESAS ================== */}
              <Typography variant="body2" paragraph>
                <strong>1. Aceitação dos Termos</strong><br />
                Ao registrar-se como empresa parceira e utilizar qualquer funcionalidade ou serviço da nossa plataforma,
                a empresa declara que leu, compreendeu e concorda plenamente com os Termos de Serviço aqui descritos.
                A aceitação destes Termos é um requisito indispensável para o uso da plataforma e representa consentimento
                pleno e irrevogável.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>2. Obrigações da Empresa Parceira</strong><br />
                As empresas parceiras comprometem-se a:
              </Typography>
              <ul>
                <li>
                  Fornecer informações cadastrais verdadeiras, completas e atualizadas, incluindo, mas não se limitando a:
                  CNPJ, endereço, telefone, e-mail e dados bancários.
                </li>
                <li>
                  Oferecer serviços de qualidade, garantindo que o conteúdo publicado na plataforma seja uma representação
                  fiel e precisa dos serviços prestados.
                </li>
                <li>
                  Cumprir integralmente todas as leis e regulamentações aplicáveis ao setor de atuação, incluindo normas
                  trabalhistas, tributárias, consumeristas e ambientais.
                </li>
                <li>
                  Respeitar os direitos dos consumidores, tratando-os de maneira ética, justa e profissional.
                </li>
              </ul>
              <Typography variant="body2" paragraph>
                Qualquer violação das obrigações acima pode resultar em suspensão ou encerramento imediato da conta da
                empresa, sem aviso prévio.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>3. Publicação e Gestão de Serviços</strong><br />
                Ao publicar serviços na plataforma, a empresa concorda que:
              </Typography>
              <ul>
                <li>
                  Todo o conteúdo (incluindo descrições, preços e condições) será revisado e aprovado pela plataforma antes
                  da publicação.
                </li>
                <li>
                  Os serviços publicados estão sujeitos a moderação contínua, podendo ser removidos ou alterados a qualquer
                  momento se não cumprirem os padrões estabelecidos.
                </li>
                <li>
                  Os preços apresentados devem incluir quaisquer impostos, taxas ou encargos aplicáveis. Caso contrário, a
                  empresa é integralmente responsável por arcar com esses valores.
                </li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>4. Suspensão e Encerramento de Contas</strong><br />
                A plataforma reserva-se o direito de, a qualquer momento e sem aviso prévio:
              </Typography>
              <ul>
                <li>Suspender ou encerrar contas de empresas que violem estes Termos ou qualquer lei aplicável.</li>
                <li>Encerrar contas associadas a práticas fraudulentas, má conduta, violações contratuais ou qualquer
                  atividade considerada prejudicial à integridade da plataforma ou de seus usuários.</li>
                <li>Empresas com contas encerradas podem ter seu CNPJ incluído em uma lista de restrição, impedindo futuros
                  cadastros na plataforma.</li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>5. Restrições de Uso</strong><br />
                As empresas concordam em não utilizar a plataforma para:
              </Typography>
              <ul>
                <li>Práticas enganosas, fraudulentas ou ilegais.</li>
                <li>Veiculação de conteúdo impróprio, ofensivo ou que viole direitos autorais, marcas registradas ou
                  quaisquer outros direitos de propriedade intelectual.</li>
                <li>Manipular avaliações ou feedbacks de usuários de forma a obter vantagens indevidas.</li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>6. Política de Denúncias e Penalidades</strong><br />
                Usuários da plataforma têm o direito de denunciar empresas por práticas inadequadas ou violações destes
                Termos.
              </Typography>
              <Typography variant="body2" paragraph>
                A plataforma compromete-se a investigar todas as denúncias de forma imparcial e criteriosa.
                Caso sejam constatadas irregularidades, penalidades podem incluir desde advertências formais até a remoção
                de serviços, suspensão temporária ou encerramento definitivo da conta da empresa.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>7. Responsabilidade pelas Transações</strong><br />
                As empresas reconhecem que:
              </Typography>
              <ul>
                <li>São integralmente responsáveis pelos serviços oferecidos e pela execução das transações realizadas
                  por meio da plataforma.</li>
                <li>Qualquer reclamação ou disputa relacionada aos serviços prestados será tratada inicialmente pela
                  empresa, com suporte da plataforma, caso necessário.</li>
                <li>O pagamento recebido pelas transações está sujeito a retenções ou encargos operacionais estabelecidos
                  pela plataforma, detalhados em contratos específicos.</li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>8. Limitação de Responsabilidade da Plataforma</strong><br />
                A plataforma atua exclusivamente como intermediária entre usuários e empresas. Portanto:
              </Typography>
              <ul>
                <li>Não se responsabiliza pela qualidade, execução ou entrega dos serviços oferecidos pelas empresas.</li>
                <li>Não é responsável por prejuízos, perdas financeiras ou danos decorrentes de interações entre empresas
                  e usuários.</li>
                <li>A plataforma reserva-se o direito de isentar-se de qualquer litígio judicial, devendo ser contatada
                  previamente para negociações e acordos extrajudiciais.</li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>9. Arbitragem Obrigatória</strong><br />
                Qualquer disputa ou controvérsia relacionada ao uso da plataforma por empresas parceiras deverá ser resolvida
                exclusivamente por meio de arbitragem obrigatória.
              </Typography>
              <Typography variant="body2" paragraph>
                Ao aceitar estes Termos, a empresa renuncia ao direito de processar a plataforma diretamente em tribunais judiciais.
                Qualquer tentativa de litígio judicial antes da arbitragem será considerada uma violação contratual.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>10. Taxas e Retenções</strong><br />
                As empresas concordam que:
              </Typography>
              <ul>
                <li>A plataforma pode cobrar taxas de serviço ou comissões sobre as transações realizadas, conforme estipulado
                  nos contratos específicos.</li>
                <li>Todas as taxas são consideradas finais e irrevogáveis, salvo em casos de erro comprovado pela plataforma.</li>
              </ul>

              <Typography variant="body2" paragraph>
                <strong>11. Confidencialidade e Proteção de Dados</strong><br />
                As empresas comprometem-se a:
              </Typography>
              <ul>
                <li>Manter em sigilo todas as informações confidenciais da plataforma, incluindo dados de usuários, políticas
                  internas e relatórios operacionais.</li>
                <li>Cumprir integralmente as disposições da Lei Geral de Proteção de Dados (LGPD) e garantir que qualquer
                  dado pessoal tratado seja utilizado exclusivamente para os fins previstos.</li>
              </ul>
              <Typography variant="body2" paragraph>
                Qualquer vazamento de informações confidenciais resultará em penalidades severas, incluindo multas contratuais
                e ações legais.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>12. Cooperação no Combate a Fraudes</strong><br />
                A plataforma pode solicitar informações adicionais, auditorias ou documentos para verificar a legitimidade
                das empresas cadastradas.
              </Typography>
              <Typography variant="body2" paragraph>
                A não colaboração ou recusa em fornecer os dados solicitados poderá resultar em suspensão imediata da conta.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>13. Alterações nos Termos de Serviço</strong><br />
                A plataforma reserva-se o direito de alterar estes Termos a qualquer momento, sem aviso prévio.
              </Typography>
              <Typography variant="body2" paragraph>
                É responsabilidade da empresa consultar regularmente os Termos para estar ciente de possíveis mudanças.
                O uso contínuo da plataforma após alterações nos Termos será considerado como aceitação das novas condições.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>14. Considerações Finais</strong><br />
                A aceitação destes Termos é irrevogável e constitui um acordo juridicamente vinculante entre a empresa e
                a plataforma.
              </Typography>
              <Typography variant="body2" paragraph>
                Ao continuar utilizando a plataforma, a empresa reconhece que:
              </Typography>
              <ul>
                <li>Está ciente de todas as cláusulas aqui descritas e concorda com sua aplicação integral.</li>
                <li>Qualquer violação destes Termos pode resultar em sanções severas, incluindo ações legais, sem prejuízo
                  de outros direitos que a plataforma venha a exercer.</li>
              </ul>
              <Typography variant="body2" paragraph>
                A plataforma reforça seu compromisso em oferecer um ambiente ético, seguro e eficiente para empresas e usuários,
                garantindo transparência e confiabilidade em todas as suas operações.
              </Typography>
            </TabPanel>

            {/* Botão para voltar */}
            <Button
              variant="contained"
              onClick={handleGoBack}
              sx={{ mt: 3 }}
            >
              Voltar
            </Button>
          </Paper>

          {/* Botão de Feedback (dark mode, etc.) */}
          <FeedbackButton />
        </Box>
      </ThemeProviderWrapper>
    </ThemeModeProvider>
  );
};

export default TermsPage;
